import React, { FC, ReactNode, useEffect, useRef } from 'react';
import './delete-account-modal.scss';

interface HandbookConfirmModalProps {
  title: string;
  heading: string;
  children: ReactNode;
  confirmLabel: string;
  busyLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  /** Extra class on the dialog, for per-use styling. */
  className?: string;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: () => void;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keyboard behaviour shared by the handbook dialogs: focus `initialFocus` on
 * open, close on Escape (unless busy), keep Tab inside the dialog and hand
 * focus back to the opener on unmount.
 */
export function useHandbookDialog(
  dialogRef: React.RefObject<HTMLElement | null>,
  initialFocus: React.RefObject<HTMLElement | null>,
  busy: boolean,
  onClose: () => void,
): void {
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    initialFocus.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (latest.current.busy) return;
        e.stopPropagation();
        latest.current.onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (
        e.shiftKey &&
        (active === first || !dialogRef.current.contains(active))
      ) {
        e.preventDefault();
        last.focus();
      } else if (
        !e.shiftKey &&
        (active === last || !dialogRef.current.contains(active))
      ) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus();
    };
  }, [dialogRef, initialFocus]);
}

/** Confirmation step before a handbook action that changes what is live. */
export const HandbookConfirmModal: FC<HandbookConfirmModalProps> = ({
  title,
  heading,
  children,
  confirmLabel,
  busyLabel,
  cancelLabel = 'Cancel',
  danger = false,
  className,
  busy,
  error,
  onClose,
  onConfirm,
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useHandbookDialog(dialogRef, cancelRef, busy, onClose);

  const handleClose = () => {
    if (!busy) onClose();
  };
  return (
    <div
      className='delete-account-modal-overlay'
      onClick={handleClose}
      data-testid='handbook-confirm-modal'
    >
      <div
        ref={dialogRef}
        className={`delete-account-modal handbook-confirm${className ? ` ${className}` : ''}`}
        role='dialog'
        aria-modal='true'
        aria-labelledby='handbook-confirm-title'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='modal-header'>
          <p className='modal-title' id='handbook-confirm-title'>
            {title}
          </p>
          <button
            type='button'
            className='close-button'
            onClick={handleClose}
            aria-label='Close'
            disabled={busy}
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>
        <div className='modal-body'>
          <h3 className='body-title'>{heading}</h3>
          <div className='body-text'>{children}</div>
        </div>
        <div className='modal-footer'>
          {error && (
            <p className='delete-error' role='alert'>
              {error}
            </p>
          )}
          <button
            ref={cancelRef}
            type='button'
            className='btn-delete-dismiss'
            onClick={handleClose}
            disabled={busy}
          >
            {cancelLabel}
          </button>
          <button
            type='button'
            className={`btn-delete-confirm${danger ? '' : ' neutral'}`}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HandbookConfirmModal;
